import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { countries } from '../../data/countries';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface ExistingBowler {
  id: number;
  name: string;
  email: string;
  country: string;
  city: string;
}

type ModalState = 'none' | 'exists' | 'similar' | 'success';

const CreateBowlerAccount: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', country: '', city: '', gender: '', dob: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [modal, setModal] = useState<ModalState>('none');
  const [existingBowler, setExistingBowler] = useState<ExistingBowler | null>(null);
  const [similarBowlers, setSimilarBowlers] = useState<Array<{ id: number; name: string; email: string | null }>>([]);
  const [countdown, setCountdown] = useState(3);

  // Auto-close success modal after 3 seconds
  useEffect(() => {
    if (modal === 'success') {
      setCountdown(3);
      let count = 3;
      countdownRef.current = setInterval(() => {
        count -= 1;
        setCountdown(count);
        if (count <= 0) {
          clearInterval(countdownRef.current!);
          setModal('none');
          handleReset();
        }
      }, 1000);
    }
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  const capFirst = (val: string) => val.length === 0 ? val : val.charAt(0).toUpperCase() + val.slice(1);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const val = (name === 'firstName' || name === 'lastName') ? capFirst(value) : value;
    setForm(prev => ({ ...prev, [name]: val }));
    setError('');
  };

  const handleReset = () => {
    setForm({ firstName: '', lastName: '', email: '', country: '', city: '', gender: '', dob: '' });
    setError('');
    setExistingBowler(null);
    setSimilarBowlers([]);
  };

  // Creates the bowler account — called either directly or after confirming similar-name warning
  const doCreate = async () => {
    setSubmitting(true);
    setError('');
    try {
      const createRes = await fetch(`${BACKEND_URL}/bookie-side-action/create-bowler`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          fullName: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
          email: form.email.trim() || null,
          country: form.country,
          city: form.city.trim(),
          gender: form.gender || null,
          dob: form.dob || null,
        }),
      });
      const createData = await createRes.json();
      if (!createRes.ok || !createData.success) {
        if (createData.exists) {
          setExistingBowler(createData.bowler);
          setModal('exists');
        } else {
          setError(createData.error || t('common.error'));
          setModal('none');
        }
        return;
      }
      setModal('success');
    } catch {
      setError(t('auth.networkError'));
      setModal('none');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError(t('bookie.createBowler.allFieldsRequired'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Step 1: Email duplicate check — only when an email was provided
      if (form.email.trim()) {
        const checkRes = await fetch(`${BACKEND_URL}/bookie-side-action/check-bowler`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ email: form.email.trim() }),
        });
        const checkData = await checkRes.json();
        if (!checkRes.ok) { setError(checkData.error || t('common.error')); return; }
        if (checkData.exists) {
          setExistingBowler(checkData.bowler);
          setModal('exists');
          return;
        }
      }

      // Step 2: Similar-name check — warn before inserting
      const simRes = await fetch(`${BACKEND_URL}/bookie-side-action/similar-bowlers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim() }),
      });
      const simData = await simRes.json();
      if (simData.success && simData.bowlers && simData.bowlers.length > 0) {
        setSimilarBowlers(simData.bowlers);
        setModal('similar');
        return;
      }

      // No similar names — create directly
      await doCreate();
    } catch {
      setError(t('auth.networkError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900">      {/* ── Similar Names Modal ─────────────────────────────────────────────── */}
      {modal === 'similar' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-yellow-400/40 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-5">
              <div className="text-5xl mb-3">🔍</div>
              <h3 className="text-xl font-bold text-white">Similar Bowlers Found</h3>
              <p className="text-yellow-300 mt-2 text-sm">We found bowlers with similar names already in the system. If any of these is the person you're trying to add, please cancel.</p>
            </div>
            <div className="space-y-2 mb-5 max-h-52 overflow-y-auto">
              {similarBowlers.map(b => (
                <div key={b.id} className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-semibold">{b.name}</p>
                    <p className="text-emerald-300 text-xs">{b.email || 'No email'}</p>
                  </div>
                  <span className="text-gray-400 text-xs">ID #{b.id + 100}</span>
                </div>
              ))}
            </div>
            <p className="text-orange-300 text-xs text-center mb-5">If any of the above is the bowler you are trying to insert, please cancel.</p>
            <div className="flex gap-3">
              <button
                onClick={() => { setModal('none'); setSimilarBowlers([]); }}
                className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-2.5 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                onClick={() => { setSimilarBowlers([]); doCreate(); }}
                disabled={submitting}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition"
              >
                {submitting ? 'Creating...' : 'No, Add Bowler'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Exists Modal ────────────────────────────────────────────────────── */}
      {modal === 'exists' && existingBowler && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-orange-400/40 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-white">{t('bookie.createBowler.existsTitle')}</h3>
              <p className="text-orange-300 mt-2 text-sm">{t('bookie.createBowler.existsMessage')}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 space-y-2 mb-6 border border-white/10">
              <p className="text-white text-sm">
                <span className="text-emerald-300 font-semibold">{t('bookie.createBowler.existsName')}: </span>
                {existingBowler.name}
              </p>
              <p className="text-white text-sm">
                <span className="text-emerald-300 font-semibold">{t('bookie.createBowler.existsEmail')}: </span>
                {existingBowler.email}
              </p>
              {existingBowler.country && (
                <p className="text-white text-sm">
                  <span className="text-emerald-300 font-semibold">{t('bookie.createBowler.existsCountry')}: </span>
                  {existingBowler.country}
                </p>
              )}
              {existingBowler.city && (
                <p className="text-white text-sm">
                  <span className="text-emerald-300 font-semibold">{t('bookie.createBowler.existsCity')}: </span>
                  {existingBowler.city}
                </p>
              )}
            </div>
            <p className="text-emerald-300 text-xs text-center mb-6">{t('bookie.createBowler.existsNote')}</p>
            <button
              onClick={() => { setModal('none'); setExistingBowler(null); }}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold py-3 rounded-xl transition duration-200"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* ── Success Modal ────────────────────────────────────────────────────── */}
      {modal === 'success' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-emerald-900 to-teal-900 border border-emerald-400/40 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="text-2xl font-bold text-white">{t('bookie.createBowler.successTitle')}</h3>
              <p className="text-emerald-200 mt-2 text-sm">{t('bookie.createBowler.successMessage')}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
              <p className="text-emerald-200 text-xs text-center">{t('bookie.createBowler.successNote')}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-emerald-400 text-sm">{t('bookie.createBowler.autoClosing', { count: countdown })}</p>
              <button
                onClick={() => { setModal('none'); handleReset(); if (countdownRef.current) clearInterval(countdownRef.current); }}
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-2 px-5 rounded-xl transition duration-200"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/bookie/dashboard')}
                className="text-emerald-200 hover:text-white transition-colors duration-200 flex items-center space-x-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="text-sm hidden sm:inline">{t('common.back')}</span>
              </button>
              <div className="w-px h-6 bg-white/20" />
              <h1 className="text-lg sm:text-xl font-bold text-white">{t('bookie.createBowler.title')}</h1>
            </div>
            <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">{user.name.charAt(0).toUpperCase()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Form ────────────────────────────────────────────────────────────── */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 sm:p-8">
          <div className="mb-6">
            <p className="text-emerald-200 text-sm">{t('bookie.createBowler.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* First Name / Last Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-white font-semibold text-sm mb-2">
                  First Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-emerald-300/50 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                />
              </div>
              <div>
                <label className="block text-white font-semibold text-sm mb-2">
                  Last Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-emerald-300/50 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-white font-semibold text-sm mb-2">
                {t('bookie.createBowler.email')} <span className="text-gray-400 font-normal text-xs">(optional)</span>
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter email address (recommended)"
                className="w-full bg-white/10 border border-white/20 text-white placeholder-emerald-300/50 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
              />
            </div>

            {/* Country */}
            <div>
              <label className="block text-white font-semibold text-sm mb-2">
                {t('bookie.createBowler.country')} <span className="text-gray-400 font-normal text-xs">(optional)</span>
              </label>
              <select
                name="country"
                value={form.country}
                onChange={handleChange}
                className="w-full bg-slate-800 border border-white/20 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
              >
                <option value="" disabled>{t('auth.selectCountry')}</option>
                {countries.map(c => (
                  <option key={c.code} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* City */}
            <div>
              <label className="block text-white font-semibold text-sm mb-2">
                {t('bookie.createBowler.city')} <span className="text-gray-400 font-normal text-xs">(optional)</span>
              </label>
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder={t('bookie.createBowler.cityPlaceholder')}
                className="w-full bg-white/10 border border-white/20 text-white placeholder-emerald-300/50 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
              />
            </div>

            {/* Gender & DOB — Optional */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-white font-semibold text-sm mb-2">
                  Gender <span className="text-gray-400 font-normal text-xs">(optional)</span>
                </label>
                <select
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  className="w-full bg-slate-800 border border-white/20 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                >
                  <option value="">— Not specified —</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-white font-semibold text-sm mb-2">
                  Date of Birth <span className="text-gray-400 font-normal text-xs">(optional)</span>
                </label>
                <input
                  type="date"
                  name="dob"
                  value={form.dob}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition [color-scheme:dark]"
                />
                {form.dob && (
                  <p className="text-emerald-300 text-xs mt-1">
                    Age: {Math.floor((Date.now() - new Date(form.dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000))}
                  </p>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-900/40 border border-red-400/50 text-red-200 rounded-xl px-4 py-3 text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-xl transition duration-200 transform hover:scale-105"
            >
              {submitting ? t('bookie.createBowler.creating') : t('bookie.createBowler.submitButton')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateBowlerAccount;
